import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useTemplate } from '../../../../../queries/Templates/useTemplate';
import { useUserPreferencesMutation } from '../../../../../queries/User/useUserPreferencesMutation';
import { TextArea } from '../../../../components-v2/TextArea';
import { Button } from '../../../Button';
import { Checkbox } from '../../../Checkbox';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { socialMediaSignatureStyleSheet } from './socialMediaSignatureStyles';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const SocialMediaSignatureComposerBase: React.FC<IProps> = observer(props => {
	const { className, styles = [] } = props;
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const [signature, setSignature] = React.useState<string>('');
	const [includeDisclaimer, setIncludeDisclaimer] = React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [isSaving, setIsSaving] = React.useState<boolean>(false);
	const [validationError, setValidationError] = React.useState<string>('');
	const [hasChanges, setHasChanges] = React.useState<boolean>(false);

	// Character limit for social media disclaimer text
	const MAX_CHARACTERS = 875;

	// Get current template ID from user preferences
	const currentTemplateId = userSession?.user?.userPreferences?.socialMediaSignatureTemplateId;
	// Load specific template if one is set
	const { data: existingTemplate, isPending: isLoadingTemplate } = useTemplate({
		templateId: currentTemplateId,
		enabled: !!currentTemplateId,
		refetchOnMount: 'always',
	});
	// Mutation to update user preferences
	const { mutate: updateUserPreferences } = useUserPreferencesMutation({
		onSuccess: () => {
			// User preferences updated successfully
		},
		onError: error => {
			errorMessages.pushApiError(error, 'Failed to update user preferences');
		},
	});
	const templatesVm = React.useMemo(() => {
		return new Api.TemplatesViewModel(userSession);
	}, [userSession]);
	const saveSignature = React.useCallback(async () => {
		if (!includeDisclaimer) {
			// If disclaimer is disabled, remove the template ID from user preferences
			updateUserPreferences({
				preferences: {
					...userSession?.user?.userPreferences,
					socialMediaSignatureTemplateId: '',
				},
			});
			setHasChanges(false);
			toast.success('Social media signature disabled successfully');
			return;
		}

		if (!signature.trim()) {
			return;
		}

		setIsSaving(true);
		try {
			// Wrap content in <lev-content> as required
			const wrappedContent = `<lev-content>${signature}</lev-content>`;
			if (currentTemplateId && existingTemplate) {
				// Update existing template
				const updatedTemplate: Api.ITemplate = {
					...existingTemplate,
					id: currentTemplateId,
					content: {
						document: wrappedContent,
					},
				};

				await templatesVm.update(updatedTemplate);
			} else {
				// Create new template
				const template: Api.ITemplate = {
					name: 'Social Media Signature',
					templateType: Api.TemplateType.SocialMediaSignature,
					content: {
						document: wrappedContent,
					},
					scope: Api.TemplateScope.User,
					defaultTemplate: true,
				};

				const createdTemplate = await templatesVm.create(template);

				// Update user preferences with the new template ID
				if (createdTemplate?.id) {
					updateUserPreferences({
						preferences: {
							...userSession?.user?.userPreferences,
							socialMediaSignatureTemplateId: createdTemplate.id,
						},
					});
				}
			}

			setHasChanges(false); // Success - template created/updated and user preferences updated
			toast.success('Social media signature saved successfully');
		} catch (error) {
			errorMessages.pushApiError(error, 'Failed to save social media signature');
		} finally {
			setIsSaving(false);
		}
	}, [
		includeDisclaimer,
		signature,
		templatesVm,
		errorMessages,
		updateUserPreferences,
		userSession,
		currentTemplateId,
		existingTemplate,
	]); // Load existing signature data when the component mounts or when template data becomes available
	React.useEffect(() => {
		// If the template is still loading, wait
		if (isLoadingTemplate && !!currentTemplateId) {
			return;
		}

		// If we don't have a template ID, reset to empty state
		if (!currentTemplateId) {
			setSignature('');
			setIncludeDisclaimer(false);
			setHasChanges(false);
			return;
		}

		setIsLoading(true);
		try {
			if (existingTemplate?.content?.document) {
				// Extract content from <lev-content> wrapper
				const parser = new DOMParser();
				const doc = parser.parseFromString(existingTemplate.content.document, 'text/html');
				const levContentElement = doc.querySelector('lev-content');
				const content = levContentElement?.textContent || existingTemplate.content.document;
				setSignature(content);
				setIncludeDisclaimer(true); // If signature exists, enable checkbox
				setHasChanges(false); // Reset hasChanges when loading fresh data
			}
		} catch {
			// Failed to load existing signature
		} finally {
			setIsLoading(false);
		}
	}, [currentTemplateId, isLoadingTemplate, existingTemplate?.content?.document]);

	const handleSignatureChange = React.useCallback(
		(value: string) => {
			setSignature(value);
			setHasChanges(true);

			// Validate character limit
			if (value.length > MAX_CHARACTERS) {
				setValidationError(`Signature must be ${MAX_CHARACTERS} characters or less`);
			} else {
				setValidationError('');
			}
		},
		[MAX_CHARACTERS]
	);

	const handleCheckboxChange = React.useCallback((checked: boolean) => {
		setIncludeDisclaimer(checked);
		setHasChanges(true);
	}, []);
	const handleCancel = React.useCallback(() => {
		// Reset to original values without triggering loading
		if (existingTemplate?.content?.document) {
			try {
				const parser = new DOMParser();
				const doc = parser.parseFromString(existingTemplate.content.document, 'text/html');
				const levContentElement = doc.querySelector('lev-content');
				const content = levContentElement?.textContent || existingTemplate.content.document;
				setSignature(content);
				setIncludeDisclaimer(true);
			} catch {
				// Failed to reset signature
				setSignature('');
				setIncludeDisclaimer(false);
			}
		} else {
			setSignature('');
			setIncludeDisclaimer(false);
		}
		setHasChanges(false);
	}, [existingTemplate]);

	const handleSave = React.useCallback(() => {
		saveSignature();
	}, [saveSignature]);
	if (isLoading || (isLoadingTemplate && !!currentTemplateId)) {
		return (
			<div className={css(socialMediaSignatureStyleSheet.loadingContainer)}>
				<LoadingSpinner type='small' />
				<span>Loading signature...</span>
			</div>
		);
	}

	return (
		<div className={`${css(socialMediaSignatureStyleSheet.container, ...styles)} ${className || ''}`}>
			<div className={css(socialMediaSignatureStyleSheet.checkboxContainer)}>
				<Checkbox
					checked={includeDisclaimer}
					className={css(socialMediaSignatureStyleSheet.checkbox)}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e.target.checked)}
				>
					<span>Include disclaimer language at the bottom of each social media post</span>
				</Checkbox>
			</div>

			{includeDisclaimer && (
				<>
					<div className={css(socialMediaSignatureStyleSheet.fieldGroup)}>
						<label className={css(socialMediaSignatureStyleSheet.label)}>Disclaimer Text</label>
						<TextArea
							maxLength={MAX_CHARACTERS}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleSignatureChange(e.target.value)}
							placeholder='Enter your disclaimer text...'
							rows={4}
							styles={[socialMediaSignatureStyleSheet.textArea]}
							value={signature}
						/>
						{validationError && (
							<div className={css(socialMediaSignatureStyleSheet.errorMessage)}>{validationError}</div>
						)}
					</div>

					<div className={css(socialMediaSignatureStyleSheet.characterCountContainer)}>
						<div
							className={css(
								socialMediaSignatureStyleSheet.characterCount,
								signature.length > MAX_CHARACTERS && socialMediaSignatureStyleSheet.characterCountError
							)}
						>
							{signature.length} / {MAX_CHARACTERS} characters
						</div>
					</div>
				</>
			)}

			{hasChanges && (
				<div className={css(socialMediaSignatureStyleSheet.buttonGroup)}>
					<Button
						className={css(socialMediaSignatureStyleSheet.saveButton)}
						disabled={(includeDisclaimer && (!signature.trim() || !!validationError)) || isSaving || !hasChanges}
						isLoading={isSaving}
						onClick={handleSave}
						kind='primary'
					>
						{isSaving ? (
							<>
								<LoadingSpinner type='tiny' />
								Saving...
							</>
						) : (
							'Save'
						)}
					</Button>
					<Button
						className={css(socialMediaSignatureStyleSheet.cancelButton)}
						disabled={isSaving}
						onClick={handleCancel}
						kind='reverse'
					>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
});

export const SocialMediaSignatureComposer = inject(ImpersonationContextKey)(SocialMediaSignatureComposerBase);
